/* ================================================================
   Mehrsprachigkeit — Grundgerüst.

   Schlüssel ist der deutsche Quelltext. Das hat drei Vorteile: Der
   Code bleibt lesbar, es müssen keine künstlichen Schlüssel erfunden
   werden, und eine fehlende Übersetzung fällt automatisch auf Deutsch
   zurück statt einen kryptischen Platzhalter zu zeigen.

     t("Auf die Weinkarte setzen")
     t("Es liegen nur {n} Flaschen im Keller.", { n: 6 })
     t.plural(n, "eine Flasche", "{n} Flaschen")

   Die Kataloge für Englisch, Spanisch und Französisch werden erst bei
   Bedarf geladen — wer auf Deutsch bleibt, lädt nichts nach.
   ================================================================ */

const SPRACHEN = [
  { code: "de", name: "Deutsch", locale: "de-DE" },
  { code: "en", name: "English", locale: "en-GB" },
  { code: "es", name: "Español", locale: "es-ES" },
  { code: "fr", name: "Français", locale: "fr-FR" },
];

const IST_SPRACHE = (c) => SPRACHEN.some((s) => s.code === c);

let aktuell = "de";
let katalog = {};                 /* Schlüssel → Übersetzung */
const geladen = { de: {} };
const horcher = new Set();

const meldeAenderung = () => { for (const h of [...horcher]) h(aktuell); };

/* Platzhalter {name} ersetzen. */
function fuellen(text, werte) {
  if (!werte) return text;
  return String(text).replace(/\{(\w+)\}/g, (treffer, schluessel) =>
    Object.prototype.hasOwnProperty.call(werte, schluessel) ? String(werte[schluessel]) : treffer);
}

function t(text, werte) {
  const uebersetzt = katalog[text];
  return fuellen(uebersetzt != null && uebersetzt !== "" ? uebersetzt : text, werte);
}

/* Einzahl/Mehrzahl. Die Regeln der vier Sprachen sind hier gleich:
   genau 1 → Einzahl, sonst Mehrzahl. */
t.plural = (anzahl, einzahl, mehrzahl, werte) =>
  t(Number(anzahl) === 1 ? einzahl : mehrzahl, { n: anzahl, ...(werte || {}) });

/* Kataloge werden dynamisch geladen; esbuild bündelt sie dadurch in
   eigene Teile, die erst beim Sprachwechsel geholt werden. */
async function katalogLaden(code) {
  if (geladen[code]) return geladen[code];
  try {
    let modul;
    if (code === "en") modul = await import("./en.js");
    else if (code === "es") modul = await import("./es.js");
    else if (code === "fr") modul = await import("./fr.js");
    else return {};
    geladen[code] = modul.texte || {};
  } catch (e) {
    console.error("Sprachkatalog konnte nicht geladen werden:", code, e.message);
    geladen[code] = {};
  }
  return geladen[code];
}

async function setzeSprache(code) {
  const ziel = IST_SPRACHE(code) ? code : "de";
  katalog = await katalogLaden(ziel);
  aktuell = ziel;
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.lang = ziel;
  }
  meldeAenderung();
  return ziel;
}

const sprache = () => aktuell;
const locale = () => (SPRACHEN.find((s) => s.code === aktuell) || SPRACHEN[0]).locale;

function beiSprachwechsel(fn) {
  horcher.add(fn);
  return () => horcher.delete(fn);
}

/* Vorschlag aus der Browsereinstellung — nur als Vorbelegung. */
function browserSprache() {
  if (typeof navigator === "undefined") return "de";
  const liste = navigator.languages && navigator.languages.length
    ? navigator.languages : [navigator.language || "de"];
  for (const eintrag of liste) {
    const code = String(eintrag).slice(0, 2).toLowerCase();
    if (IST_SPRACHE(code)) return code;
  }
  return "de";
}

export { beiSprachwechsel, browserSprache, IST_SPRACHE, locale, setzeSprache, sprache, SPRACHEN, t };
